import React, { useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  documentImportApi,
  DECLARED_FORMATS,
  ACCEPT_EXTENSIONS,
  CANT_ESTIMATE_LINE,
  formatApproximateUsd,
  formatWordCount,
} from '../../api/documentImport';
import { IMPORT_NEXT } from './importNext';
import { noteOutlineChanged } from '../../hooks/useBookPath';
import { uploadPathApi } from '../../api/uploadPath';
import { storiesApi } from '../../api/stories';
import OutsideChatPromptChooser from './OutsideChatPromptChooser';
import PromptSignatureSoftNotice from './PromptSignatureSoftNotice';
import {
  digestSoftOfferForFiles,
  MAKE_NOTES_FROM_THESE_DOCS,
  OPEN_PLOT_TRACK,
  PASTE_CLEAR,
  PASTE_FROM_CHAT,
  PASTE_FROM_CHAT_HELP,
  PASTE_FROM_CHAT_PLACEHOLDER,
  PASTE_SHEET_FORMAT_LABEL,
  PROSE_CHECK_BACK,
  PROSE_CHECK_COULD_NOT_CREATE,
  PROSE_CHECK_COULD_NOT_SORT,
  PROSE_CHECK_KIND_OUTLINE,
  PROSE_CHECK_KIND_PROSE,
  PROSE_CHECK_KIND_UNSURE,
  PROSE_CHECK_NEED_CHAPTER,
  PROSE_CHECK_NO_BOOK,
  PROSE_CHECK_OUTLINE_STAYS,
  PROSE_CHECK_PART,
  PROSE_CHECK_SAVE_ONLY,
  PROSE_CHECK_STEP_READING,
  PROSE_CHECK_STEP_SORTING,
  PROSE_CHECK_TITLE,
  UPLOAD_FILE_EMPTY,
  proseCheckAddLabel,
  proseCheckSummary,
  PUT_SCENES_ON_PLOT_TRACK,
  SKIP_AND_OPEN_MY_BOOK,
  UPLOAD_A_FILE,
} from '../../utils/writerCopy';
import {
  initialKindOverrides,
  proseKindsFromReport,
  proseUploadTarget,
  sectionKey,
} from './proseUploadConfirm';
import { emptyBatchPreview, fileIsEmpty } from './uploadFileEmpty';
import {
  STORY_SHEET_FORMAT,
  batchWithPaste,
  pasteBodyIsEmpty,
} from './pasteStorySheet';
import { usePaidConfirm } from '../Common/usePaidConfirm';
import { useConfirm } from '../Common/useConfirm';
import ConfirmModal from '../Common/ConfirmModal';
import './ImportUploadModal.css';

function formatLabel(value) {
  return DECLARED_FORMATS.find((item) => item.value === value)?.label || value || '—';
}

function FormatOptions() {
  return (
    <>
      <option value="">Choose format…</option>
      {DECLARED_FORMATS.map((fmt) => (
        <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
      ))}
    </>
  );
}

function statusLabel(status) {
  if (status === 'accepted') return 'Accepted';
  if (status === 'parked') return 'Parked';
  if (status === 'rejected') return 'Rejected';
  return status;
}

function emptyDigestForm() {
  return {
    characters: '',
    plot_threads: '',
    world_details: '',
    theme: '',
    logline: '',
    scope_note: '',
  };
}

function digestToForm(digest) {
  if (!digest) return emptyDigestForm();
  return {
    characters: digest.characters || '',
    plot_threads: digest.plot_threads || '',
    world_details: digest.world_details || '',
    theme: digest.theme || '',
    logline: digest.logline || '',
    scope_note: digest.scope_note || '',
  };
}

function formToPayload(form) {
  return {
    characters: form.characters,
    plot_threads: form.plot_threads,
    world_details: form.world_details,
    theme: form.theme.trim() ? form.theme : null,
    logline: form.logline.trim() ? form.logline : null,
    scope_note: form.scope_note.trim() ? form.scope_note : null,
  };
}

function recomputePreview(preview, remainingEntries) {
  if (!preview) return null;
  // Keep rows aligned with remaining entries, including duplicate names.
  const unused = [...(preview.files || [])];
  const files = remainingEntries.map((entry) => {
    const matchIndex = unused.findIndex((file) => file.original_filename === entry.file.name);
    if (matchIndex === -1) {
      return {
        original_filename: entry.file.name,
        declared_format: entry.declaredFormat,
        word_count: 0,
        readable: false,
      };
    }
    const [match] = unused.splice(matchIndex, 1);
    return match;
  });
  const wordCount = files.reduce((sum, file) => sum + (file.word_count || 0), 0);
  let approximateCost = null;
  if (preview.can_estimate) {
    approximateCost = preview.word_count
      ? preview.approximate_cost_usd * (wordCount / preview.word_count)
      : 0;
  }
  return {
    ...preview,
    files,
    word_count: wordCount,
    approximate_cost_usd: approximateCost,
  };
}

function fileHasReadableWords(file) {
  if (!file || file.readable === false) return false;
  const words = Number(file.word_count);
  return Number.isFinite(words) && words > 0;
}

function previewHasReadableWords(preview) {
  const files = preview?.files;
  if (Array.isArray(files)) {
    if (!files.length) return false;
    // One empty file must not ride along with a file that has words.
    return files.every(fileHasReadableWords);
  }
  return Number(preview?.word_count) > 0;
}

const SUGGESTED_FORMATS = new Set(['manuscript_draft', 'outline']);

function shownAcceptedCount(result, sceneCount) {
  const scenes = Number(sceneCount) || 0;
  if (scenes > 0) return scenes;
  return Number(result?.accepted_count) || 0;
}

function estimateLine(preview) {
  if (!preview) return '';
  if (preview.can_estimate) {
    const amount = formatApproximateUsd(preview.approximate_cost_usd);
    return amount
      ? `Approximate embedding cost: ${amount}. This is an estimate, not an exact cost.`
      : CANT_ESTIMATE_LINE;
  }
  return preview.cost_unavailable_reason || CANT_ESTIMATE_LINE;
}

function FileResultList({ files }) {
  return (
    <ul className="import-upload__results">
      {(files || []).map((file, index) => (
        <li key={`${index}-${file.original_filename}`} className={`import-upload__result is-${file.status}`}>
          <span className="import-upload__result-name">{file.original_filename}</span>
          <span className="import-upload__result-status">
            {statusLabel(file.status)}
            {file.declared_format ? ` · ${formatLabel(file.declared_format)}` : ''}
          </span>
          {file.reason && (
            <span className="import-upload__result-reason">{file.reason}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Shared upload flow for both doors (#627) with cost preview (#628),
 * invisible ingest (#629), one batch digest (#630), and the Tier 1
 * results screen after approve (#631 / #782). Fixed CTAs route to
 * Book Notes, Series Notes when the project is a series, the long
 * write-up behind a card, or Focus. No generation here. No Story
 * Board button. Writer-facing copy does not name a Dossier room.
 * Series notes stay in Series Notes. They do not open book-level
 * Book Notes or create a story.
 * New Project and the Upload side door pass showPromptChooser so
 * Prompt A/B is on screen before the file step (#1268). Docs already
 * mounts the same chooser beside Bring a story.
 * Paste from chat (#1274) is a second intake on this same modal.
 * A non-empty paste is one more Markdown file on the batch. It uses
 * the same preview and ingest calls as a chosen .md file.
 */
function ImportUploadModal({
  project,
  storyId = null,
  onClose,
  showPromptChooser = false,
}) {
  const inputRef = useRef(null);
  const pasteTitleId = useId();
  const uploadTitleId = useId();
  const pasteHelpId = useId();
  const needChapterId = useId();
  const [entries, setEntries] = useState([]);
  const [pasteText, setPasteText] = useState('');
  const [pasteFormat, setPasteFormat] = useState('');
  const [frozenPastes, setFrozenPastes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { ask: askPaid, dialog: paidDialog } = usePaidConfirm();
  const { confirm, modalProps } = useConfirm();
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestForm, setDigestForm] = useState(emptyDigestForm());
  const [closedToPath, setClosedToPath] = useState(false);
  const [softOffer, setSoftOffer] = useState(null);
  const [landedOnTrack, setLandedOnTrack] = useState(false);
  const [resolvedStoryId, setResolvedStoryId] = useState(storyId || null);
  const [bookLookupSettled, setBookLookupSettled] = useState(Boolean(storyId));
  const [proseReport, setProseReport] = useState(null);
  const [kindOverrides, setKindOverrides] = useState({});
  const [proseStep, setProseStep] = useState('');
  const [couldNotSort, setCouldNotSort] = useState(false);
  const [confirmedSceneCount, setConfirmedSceneCount] = useState(0);
  const soleLookup = useRef({ done: false, id: null });
  const pendingLandRef = useRef(null);

  const batch = useMemo(
    () => batchWithPaste({ entries, frozenPastes, pasteText, pasteFormat }),
    [entries, frozenPastes, pasteText, pasteFormat],
  );

  const ready = useMemo(
    () => batch.length > 0 && batch.every((entry) => entry.declaredFormat),
    [batch],
  );

  const applyDigest = (next) => {
    setDigest(next || null);
    setDigestForm(digestToForm(next));
  };

  const addFiles = (fileList) => {
    const next = Array.from(fileList || []).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
      file,
      declaredFormat: '',
      formatTouched: false,
      emptyFile: !file || file.size === 0,
    }));
    setEntries((prev) => [...prev, ...next]);
    setError(null);
    setPreview(null);
    const applyAddedFiles = async () => {
      const flags = await Promise.all(next.map((entry) => (
        entry.emptyFile ? true : fileIsEmpty(entry.file)
      )));
      setEntries((prev) => prev.map((entry) => {
        const index = next.findIndex((item) => item.id === entry.id);
        if (index < 0 || !flags[index]) return entry;
        return { ...entry, emptyFile: true };
      }));
      const readable = next.filter((_, index) => !flags[index]);
      if (!project?.id || !readable.length || !documentImportApi.suggestFormat) return;
      documentImportApi.suggestFormat(project.id, readable.map((entry) => entry.file))
        .then((payload) => {
          const suggestions = payload?.files || [];
          setEntries((prev) => prev.map((entry) => {
            const index = readable.findIndex((item) => item.id === entry.id);
            if (index < 0 || entry.formatTouched || entry.declaredFormat || entry.emptyFile) {
              return entry;
            }
            const suggested = suggestions[index]?.suggested_format;
            if (!SUGGESTED_FORMATS.has(suggested)) return entry;
            return { ...entry, declaredFormat: suggested };
          }));
        })
        .catch(() => {});
    };
    void applyAddedFiles();
  };

  const setFormat = (id, declaredFormat) => {
    setEntries((prev) => prev.map((entry) => (
      entry.id === id ? { ...entry, declaredFormat, formatTouched: true } : entry
    )));
    setPreview(null);
  };

  const previewFrom = (next) => {
    setPreview((current) => (current ? recomputePreview(current, next) : null));
  };

  const removeEntry = (id) => {
    const nextEntries = entries.filter((entry) => entry.id !== id);
    setEntries(nextEntries);
    previewFrom(batchWithPaste({
      entries: nextEntries,
      frozenPastes,
      pasteText,
      pasteFormat,
    }));
  };

  const onPasteText = (value) => {
    const wasEmpty = pasteBodyIsEmpty(pasteText);
    const nowEmpty = pasteBodyIsEmpty(value);
    setPasteText(value);
    setPreview(null);
    if (nowEmpty) {
      setPasteFormat('');
      return;
    }
    if (wasEmpty) {
      setPasteFormat(STORY_SHEET_FORMAT);
    }
  };

  const clearLivePaste = () => {
    setPasteText('');
    setPasteFormat('');
    setPreview(null);
  };

  const onPasteReplace = (event) => {
    if (pasteBodyIsEmpty(pasteText)) return;
    const data = event.clipboardData;
    const incoming = data?.getData('text/plain') || data?.getData('text') || '';
    if (pasteBodyIsEmpty(incoming)) return;
    const el = event.currentTarget;
    const replacesAll = el.selectionStart === 0 && el.selectionEnd === pasteText.length;
    if (!replacesAll) return;
    event.preventDefault();
    const id = `paste-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setFrozenPastes((prev) => [...prev, { id, text: pasteText, format: pasteFormat }]);
    setPasteText(incoming);
    setPasteFormat(STORY_SHEET_FORMAT);
    setPreview(null);
  };

  const setFrozenFormat = (id, declaredFormat) => {
    setFrozenPastes((prev) => prev.map((paste) => (
      paste.id === id ? { ...paste, format: declaredFormat } : paste
    )));
    setPreview(null);
  };

  const removeFrozenPaste = (id) => {
    const nextFrozen = frozenPastes.filter((paste) => paste.id !== id);
    setFrozenPastes(nextFrozen);
    previewFrom(batchWithPaste({
      entries,
      frozenPastes: nextFrozen,
      pasteText,
      pasteFormat,
    }));
  };

  const removeBatchItem = (item) => {
    if (item.id === 'paste-live') {
      setPasteText('');
      setPasteFormat('');
      previewFrom(batchWithPaste({
        entries,
        frozenPastes,
        pasteText: '',
        pasteFormat: '',
      }));
      return;
    }
    if (item.source === 'paste') {
      removeFrozenPaste(item.id);
      return;
    }
    removeEntry(item.id);
  };

  const payloadFiles = () => batch.map((entry) => ({
    file: entry.file,
    declaredFormat: entry.declaredFormat,
  }));

  // Create→Import passes the book from POST /projects. If that id is
  // missing, a project with exactly one story is still that book.
  // Several books stay unbound — do not guess a series target.
  const resolveUploadStoryId = async () => {
    if (storyId) {
      setBookLookupSettled(true);
      return storyId;
    }
    if (soleLookup.current.done) {
      setBookLookupSettled(true);
      return soleLookup.current.id;
    }
    if (!project?.id) {
      setBookLookupSettled(true);
      return null;
    }
    try {
      const stories = await storiesApi.list(project.id);
      const rows = Array.isArray(stories) ? stories : [];
      const target = proseUploadTarget({ stories: rows, lookupSettled: true });
      soleLookup.current = { done: true, id: target.bookId };
      setResolvedStoryId(target.bookId);
      setBookLookupSettled(true);
      return target.bookId;
    } catch {
      setBookLookupSettled(true);
      return null;
    }
  };

  const handleContinue = async () => {
    if (!project?.id || !ready || isSubmitting) return;
    const flags = await Promise.all(batch.map((entry) => fileIsEmpty(entry.file)));
    if (flags.some(Boolean)) {
      setEntries((prev) => prev.map((entry) => {
        const index = batch.findIndex((item) => item.id === entry.id && item.source === 'file');
        if (index < 0 || !flags[index]) return entry;
        return { ...entry, emptyFile: true };
      }));
    }
    if (flags.length > 0 && flags.every(Boolean)) {
      setError(null);
      setPreview(emptyBatchPreview(batch));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const [payload] = await Promise.all([
        documentImportApi.previewBatch(project.id, payloadFiles()),
        resolveUploadStoryId(),
      ]);
      setPreview(payload);
    } catch (err) {
      setError(err.message || 'Could not estimate this batch.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadBatch = async (report) => {
    if (!project?.id || !ready || isSubmitting) return;
    if (entries.some((entry) => entry.emptyFile)) return;
    if (!report && preview && !previewHasReadableWords(preview)) return;
    const ok = await askPaid('import_digest');
    if (!ok) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const uploadStoryId = await resolveUploadStoryId();
      const plan = report ? proseKindsFromReport(report, kindOverrides) : null;
      const wantsScenes = Boolean(plan && plan.counts.prose > 0 && uploadStoryId);
      const payload = await documentImportApi.createBatch(
        project.id,
        payloadFiles(),
        {
          storyId: uploadStoryId || undefined,
          proseDecisions: wantsScenes ? plan.decisions : undefined,
        },
      );
      setConfirmedSceneCount(wantsScenes ? plan.counts.prose : 0);
      pendingLandRef.current = wantsScenes
        ? { decisions: plan.decisions, storyId: uploadStoryId }
        : null;
      setCouldNotSort(false);
      setProseReport(null);
      setProseStep('');
      setResult(payload);
      applyDigest(payload.digest);
      if (payload.digest_error) setError(payload.digest_error);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceed = async () => {
    if (!project?.id || !ready || isSubmitting) return;
    if (entries.some((entry) => entry.emptyFile)) return;
    if (preview && !previewHasReadableWords(preview)) return;
    setIsSubmitting(true);
    setError(null);
    setCouldNotSort(false);
    setProseStep('reading');
    try {
      const uploadStoryId = await resolveUploadStoryId();
      const report = await documentImportApi.proseCheck(project.id, payloadFiles(), {
        storyId: uploadStoryId || undefined,
      });
      setProseStep('sorting');
      if (report?.needs_confirm) {
        setKindOverrides(initialKindOverrides(report));
        setProseReport(report);
        return;
      }
    } catch (err) {
      // A failed check must not block a notes upload. Manuscripts stop
      // here so a chapter file is not stored with no confirm.
      const manuscript = batch.some((entry) => entry.declaredFormat === 'manuscript_draft');
      if (manuscript) {
        setCouldNotSort(true);
        setError(PROSE_CHECK_COULD_NOT_SORT);
        setProseStep('');
        return;
      }
    } finally {
      setIsSubmitting(false);
    }
    setProseStep('');
    await uploadBatch(null);
  };

  const handleConfirmProse = () => {
    const report = proseReport;
    void uploadBatch(report);
  };

  const handleSaveOnly = () => {
    if (preview && !proseReport && !previewHasReadableWords(preview)) return;
    pendingLandRef.current = null;
    setConfirmedSceneCount(0);
    void uploadBatch(null);
  };

  const setSectionKind = (fileIndex, index, kind) => {
    setKindOverrides((prev) => ({
      ...prev,
      [sectionKey(fileIndex, index)]: kind,
    }));
  };

  const handleRetryDigest = async () => {
    if (!project?.id || !result?.batch_id || isSubmitting) return;
    const ok = await askPaid('import_digest', { title: 'Read these docs again' });
    if (!ok) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const payload = await documentImportApi.generateDigest(project.id, result.batch_id);
      applyDigest(payload);
      setResult((current) => (
        current ? { ...current, digest: payload, digest_error: null } : current
      ));
    } catch (err) {
      setError(err.message || 'Could not write the digest.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!project?.id || !result?.batch_id || isSubmitting || digest?.approved) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const approveBody = formToPayload(digestForm);
      const knownBook = storyId || result.story_id || await resolveUploadStoryId() || null;
      if (knownBook) approveBody.story_id = knownBook;
      const saved = await documentImportApi.approveDigest(
        project.id,
        result.batch_id,
        approveBody,
      );
      const plan = pendingLandRef.current;
      pendingLandRef.current = null;
      if (plan?.storyId) {
        try {
          await documentImportApi.landProseScenes(project.id, payloadFiles(), {
            storyId: plan.storyId,
            decisions: plan.decisions,
            batchId: result.batch_id,
          });
          noteOutlineChanged();
        } catch (err) {
          setError(err?.message || PROSE_CHECK_COULD_NOT_CREATE);
        }
      }
      // Paid generate does not approve. Only this free action does.
      applyDigest({
        ...(digest || {}),
        ...(saved && typeof saved === 'object' ? saved : {}),
        approved: true,
      });
      const batchStoryId = saved?.story_id || result.story_id || null;
      const bookId = batchStoryId || knownBook || resolvedStoryId || null;
      const pathA = (result.files || []).some((file) => file?.prompt_path_kind === 'A');
      let landed = false;
      if (pathA && saved?.land_error) {
        setError(saved.land_error);
      } else if (pathA && batchStoryId) {
        // The batch book is the source of truth. Do not claim the list
        // landed until the approve response says the stubs exist.
        landed = Number(saved?.scenes_landed) > 0 || Boolean(saved?.already_on_plot_track);
      } else if (pathA && bookId) {
        try {
          const placed = await uploadPathApi.importScenes(bookId, {
            projectId: project.id,
            batchIds: [result.batch_id],
          });
          landed = Number(placed?.imported_count) > 0 || Boolean(placed?.already_on_plot_track);
          if (!landed) {
            setError('Could not put these scenes on Plot Grid.');
          }
        } catch (err) {
          setError(err?.message || 'Could not put these scenes on Plot Grid.');
        }
      }
      setLandedOnTrack(landed);
      setSoftOffer({
        batch_id: result.batch_id,
        story_id: bookId,
      });
    } catch (err) {
      setError(err.message || 'Could not approve the digest.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const setField = (key, value) => {
    setDigestForm((prev) => ({ ...prev, [key]: value }));
  };

  const closeWith = (payload) => {
    onClose?.(payload && payload.next ? payload : undefined);
  };

  const closeToPath = () => {
    noteOutlineChanged();
    setClosedToPath(true);
    closeWith({
      next: IMPORT_NEXT.PATH,
      story_id: softOffer?.story_id || result?.story_id || storyId || resolvedStoryId || null,
    });
  };

  const closeToDocsOffers = () => {
    setClosedToPath(true);
    closeWith({
      next: IMPORT_NEXT.DOCS_OFFERS,
      batch_id: softOffer?.batch_id || result?.batch_id,
      story_id: softOffer?.story_id || result?.story_id || storyId || resolvedStoryId || null,
      project_id: project?.id,
    });
  };

  const bookId = softOffer?.story_id || result?.story_id || storyId || resolvedStoryId || null;
  const pathA = (result?.files || []).some((file) => file?.prompt_path_kind === 'A');

  const closeToPlotTrack = () => {
    setClosedToPath(true);
    closeWith({
      next: IMPORT_NEXT.PLOT_TRACK,
      story_id: bookId,
    });
  };

  const placeOnPlotTrack = async () => {
    if (!bookId || !project?.id || !result?.batch_id || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await uploadPathApi.importScenes(bookId, {
        projectId: project.id,
        batchIds: [result.batch_id],
      });
      setLandedOnTrack(true);
      setClosedToPath(true);
      closeWith({
        next: IMPORT_NEXT.PLOT_TRACK,
        story_id: bookId,
      });
    } catch (err) {
      setError(err?.message || 'Could not put these scenes on Plot Grid.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const abandonReview = () => {
    const reviewing = Boolean(digest) && !softOffer && !digest.approved;
    if (!reviewing) {
      pendingLandRef.current = null;
      closeWith();
      return;
    }
    confirm({
      title: 'Leave without approving?',
      message: 'This digest stays unapproved. You can approve it later from Docs.',
      confirmLabel: 'Leave',
    }).then((ok) => {
      if (ok) {
        pendingLandRef.current = null;
        closeWith();
      }
    });
  };

  const onBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;
    abandonReview();
  };

  const digestApproved = Boolean(softOffer || digest?.approved);
  const promptRedirect = (result?.files || []).some((file) => file.prompt_redirect);
  const choosingPrompt = showPromptChooser && !digest && !result && !preview && !proseReport;
  const intake = !digest && !result && !preview && !proseReport;
  const proseChoice = proseReport ? proseKindsFromReport(proseReport, kindOverrides) : null;
  const proseSceneCount = proseChoice?.counts?.prose || 0;
  const bookTarget = proseUploadTarget({
    storyId: storyId || null,
    stories: !storyId && resolvedStoryId ? [{ id: resolvedStoryId }] : [],
    lookupSettled: Boolean(storyId) || bookLookupSettled,
  });
  const noBook = bookTarget.noBook;
  const manuscriptInBatch = batch.some((entry) => entry.declaredFormat === 'manuscript_draft');
  const showNoBook = noBook && (
    Boolean(proseReport) || (manuscriptInBatch && Boolean(preview))
  );
  const addScenesDisabled = isSubmitting || proseSceneCount === 0 || noBook;
  const title = proseReport
    ? PROSE_CHECK_TITLE
    : digestApproved
      ? 'Digest approved'
      : digest
        ? 'Review digest'
        : result
          ? 'Files accepted / parked'
          : preview
            ? 'Cost preview'
            : 'Upload existing work';

  if (closedToPath) return null;

  return createPortal(
    <>
    <div className="import-upload__backdrop" onClick={onBackdropClick}>
      {paidDialog}
      <div
        className={`import-upload${digest || choosingPrompt || proseReport ? ' import-upload--digest' : ''}${intake ? ' import-upload--intake' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-upload-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="import-upload__header">
          <p className="import-upload__eyebrow">Import</p>
          <h2 id="import-upload-title">{title}</h2>
          <button
            type="button"
            className="import-upload__close"
            onClick={abandonReview}
            aria-label="Close upload"
          >
            ×
          </button>
        </div>

        {proseReport ? (
          <div className="import-upload__content">
            <ol className="import-upload__steps">
              <li>{PROSE_CHECK_STEP_READING}</li>
              <li>{PROSE_CHECK_STEP_SORTING}</li>
            </ol>
            <p className="import-upload__lede">{proseCheckSummary(proseChoice.counts)}</p>
            {showNoBook && (
              <p className="import-upload__note" role="status">{PROSE_CHECK_NO_BOOK}</p>
            )}
            {(proseChoice.counts.outline > 0 || proseChoice.counts.unsure > 0) && (
              <p className="import-upload__note">{PROSE_CHECK_OUTLINE_STAYS}</p>
            )}
            <ul className="import-upload__sections">
              {(proseReport.files || []).map((file) => (
                <li key={file.file_index}>
                  <p className="import-upload__filename">{file.original_filename}</p>
                  <ul>
                    {(file.sections || []).map((section) => {
                      const label = section.heading || section.excerpt || 'Section';
                      return (
                        <li key={section.index} className="import-upload__section">
                          <span className="import-upload__section-name">{label}</span>
                          {section.excerpt && section.heading ? (
                            <span className="import-upload__file-size">{section.excerpt}</span>
                          ) : null}
                          {section.role === 'part' ? (
                            <span className="import-upload__note">{PROSE_CHECK_PART}</span>
                          ) : (
                            <label className="import-upload__format">
                              <select
                                aria-label={label}
                                value={kindOverrides[sectionKey(file.file_index, section.index)] || section.kind}
                                onChange={(event) => setSectionKind(
                                  file.file_index,
                                  section.index,
                                  event.target.value,
                                )}
                              >
                                <option value="prose">{PROSE_CHECK_KIND_PROSE}</option>
                                <option value="outline">{PROSE_CHECK_KIND_OUTLINE}</option>
                                <option value="unsure">{PROSE_CHECK_KIND_UNSURE}</option>
                              </select>
                            </label>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
            {error && <p className="import-upload__error" role="alert">{error}</p>}
          </div>
        ) : digestApproved ? (
          <div className="import-upload__content">
            <p className="import-upload__lede">
              {digestSoftOfferForFiles(result?.files, { landed: landedOnTrack })}
            </p>
            {error && <p className="import-upload__error" role="alert">{error}</p>}
          </div>
        ) : digest ? (
          <div className="import-upload__content">
            {promptRedirect && <PromptSignatureSoftNotice />}
            <p className="import-upload__lede">
              One digest for the whole batch. Edit anything that looks
              off, then approve. Approval is the only gate.
            </p>
            {result && (
              <p className="import-upload__estimate">
                {shownAcceptedCount(result, confirmedSceneCount)} accepted
                {result.parked_count ? ` · ${result.parked_count} parked` : ''}
                {result.rejected_count ? ` · ${result.rejected_count} rejected` : ''}
                .
              </p>
            )}
            <label className="import-upload__field">
              <span>Apparent characters</span>
              <textarea
                value={digestForm.characters}
                onChange={(e) => setField('characters', e.target.value)}
                disabled={isSubmitting}
              />
            </label>
            <label className="import-upload__field">
              <span>Plot threads</span>
              <textarea
                value={digestForm.plot_threads}
                onChange={(e) => setField('plot_threads', e.target.value)}
                disabled={isSubmitting}
              />
            </label>
            <label className="import-upload__field">
              <span>World details</span>
              <textarea
                value={digestForm.world_details}
                onChange={(e) => setField('world_details', e.target.value)}
                disabled={isSubmitting}
              />
            </label>
            <label className="import-upload__field">
              <span>Theme (only if there is signal)</span>
              <textarea
                value={digestForm.theme}
                onChange={(e) => setField('theme', e.target.value)}
                disabled={isSubmitting}
              />
            </label>
            <label className="import-upload__field">
              <span>Logline (only if there is signal)</span>
              <textarea
                value={digestForm.logline}
                onChange={(e) => setField('logline', e.target.value)}
                disabled={isSubmitting}
              />
            </label>
            <label className="import-upload__field">
              <span>Series vs this book</span>
              <textarea
                value={digestForm.scope_note}
                onChange={(e) => setField('scope_note', e.target.value)}
                disabled={isSubmitting}
              />
            </label>
            <p className="import-upload__note">
              Series-versus-novel is a note you can edit, not a filing.
              Approving does not convert this project to a series.
            </p>
            {error && <p className="import-upload__error" role="alert">{error}</p>}
          </div>
        ) : result ? (
          <div className="import-upload__content">
            {promptRedirect && <PromptSignatureSoftNotice />}
            <p className="import-upload__lede">
              {result.accepted_count} accepted
              {result.parked_count ? ` · ${result.parked_count} parked` : ''}
              {result.rejected_count ? ` · ${result.rejected_count} rejected` : ''}
              {result.accepted_count
                ? '. Files are stored. The digest still needs to be written.'
                : '. Nothing to digest — no accepted files.'}
            </p>
            <FileResultList files={result.files} />
            {error && <p className="import-upload__error" role="alert">{error}</p>}
          </div>
        ) : preview ? (
          <div className="import-upload__content">
            <p className="import-upload__lede">
              This batch is about {formatWordCount(preview.word_count)} words.
            </p>
            {previewHasReadableWords(preview) && (
              <p className="import-upload__estimate" data-testid="import-cost-estimate">
                {estimateLine(preview)}
              </p>
            )}
            {showNoBook && (
              <p className="import-upload__note" role="status">{PROSE_CHECK_NO_BOOK}</p>
            )}
            {isSubmitting && proseStep && (
              <ol className="import-upload__steps">
                <li>{PROSE_CHECK_STEP_READING}</li>
                <li>{proseStep === 'sorting' ? PROSE_CHECK_STEP_SORTING : ''}</li>
              </ol>
            )}
            <ul className="import-upload__files">
              {batch.map((entry, index) => {
                const filePreview = (preview.files || [])[index];
                return (
                  <li key={entry.id} className="import-upload__file">
                    <div className="import-upload__file-meta">
                      <span className="import-upload__filename">{entry.file.name}</span>
                      <button
                        type="button"
                        className="import-upload__remove"
                        onClick={() => removeBatchItem(entry)}
                        disabled={isSubmitting}
                      >
                        Remove
                      </button>
                    </div>
                    <span className="import-upload__file-size">
                      {formatLabel(entry.declaredFormat)}
                      {filePreview ? ` · ${formatWordCount(filePreview.word_count)} words` : ''}
                    </span>
                    {(filePreview?.reason || (filePreview && !fileHasReadableWords(filePreview))) && (
                      <span className="import-upload__result-reason">
                        {filePreview?.reason || UPLOAD_FILE_EMPTY}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            {error && <p className="import-upload__error" role="alert">{error}</p>}
          </div>
        ) : (
          <div className="import-upload__content">
            {choosingPrompt && (
              <div className="import-upload__chooser">
                <OutsideChatPromptChooser />
              </div>
            )}
            <p className="import-upload__lede">
              Upload a file or paste from chat, and declare what each one is.
              txt, md, docx, or PDF as text. Unreadable files are parked, not guessed.
            </p>

            <div className="import-upload__intake">
              <section
                className="import-upload__column import-upload__column--paste"
                aria-labelledby={pasteTitleId}
              >
                <h3 id={pasteTitleId} className="import-upload__column-title">{PASTE_FROM_CHAT}</h3>
                <p className="import-upload__help" id={pasteHelpId}>{PASTE_FROM_CHAT_HELP}</p>
                <textarea
                  className="import-upload__paste"
                  rows={14}
                  placeholder={PASTE_FROM_CHAT_PLACEHOLDER}
                  aria-label={PASTE_FROM_CHAT}
                  aria-describedby={pasteHelpId}
                  value={pasteText}
                  onChange={(e) => onPasteText(e.target.value)}
                  onPaste={onPasteReplace}
                  disabled={isSubmitting}
                />
                {!pasteBodyIsEmpty(pasteText) && (
                  <button
                    type="button"
                    className="import-upload__clear"
                    onClick={clearLivePaste}
                    disabled={isSubmitting}
                  >
                    {PASTE_CLEAR}
                  </button>
                )}
                {!pasteBodyIsEmpty(pasteText) && (
                  <label className="import-upload__format">
                    <span>{PASTE_SHEET_FORMAT_LABEL}</span>
                    <select
                      value={pasteFormat}
                      onChange={(e) => {
                        setPasteFormat(e.target.value);
                        setPreview(null);
                      }}
                      disabled={isSubmitting}
                    >
                      <FormatOptions />
                    </select>
                  </label>
                )}
                {frozenPastes.length > 0 && (
                  <ul className="import-upload__files">
                    {batch.filter((item) => item.source === 'paste' && item.id !== 'paste-live').map((item) => (
                      <li key={item.id} className="import-upload__file">
                        <div className="import-upload__file-meta">
                          <span className="import-upload__filename">{item.file.name}</span>
                          <button
                            type="button"
                            className="import-upload__remove"
                            onClick={() => removeFrozenPaste(item.id)}
                            disabled={isSubmitting}
                          >
                            Remove
                          </button>
                        </div>
                        <label className="import-upload__format">
                          <span>{PASTE_SHEET_FORMAT_LABEL}</span>
                          <select
                            value={item.declaredFormat}
                            onChange={(e) => setFrozenFormat(item.id, e.target.value)}
                            disabled={isSubmitting}
                          >
                            <FormatOptions />
                          </select>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section
                className="import-upload__column import-upload__column--upload"
                aria-labelledby={uploadTitleId}
              >
                <h3 id={uploadTitleId} className="import-upload__column-title">{UPLOAD_A_FILE}</h3>
                <input
                  ref={inputRef}
                  className="import-upload__file-input"
                  type="file"
                  accept={ACCEPT_EXTENSIONS}
                  multiple
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  className="import-upload__choose"
                  onClick={() => inputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  Choose files
                </button>

                {entries.length === 0 ? (
                  <p className="import-upload__empty">No files yet.</p>
                ) : (
                  <ul className="import-upload__files">
                    {entries.map((entry) => (
                      <li key={entry.id} className="import-upload__file">
                        <div className="import-upload__file-meta">
                          <span className="import-upload__filename">{entry.file.name}</span>
                          <button
                            type="button"
                            className="import-upload__remove"
                            onClick={() => removeEntry(entry.id)}
                            disabled={isSubmitting}
                          >
                            Remove
                          </button>
                        </div>
                        <label className="import-upload__format">
                          <span>Format</span>
                          <select
                            value={entry.declaredFormat}
                            onChange={(e) => setFormat(entry.id, e.target.value)}
                            disabled={isSubmitting}
                          >
                            <FormatOptions />
                          </select>
                        </label>
                        {entry.emptyFile && (
                          <span className="import-upload__result-reason">{UPLOAD_FILE_EMPTY}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>

            {error && <p className="import-upload__error" role="alert">{error}</p>}
          </div>
        )}

        <div className="import-upload__actions">
          {digestApproved && landedOnTrack ? (
            <>
              <button
                type="button"
                className="import-upload__submit"
                onClick={closeToPlotTrack}
              >
                {OPEN_PLOT_TRACK}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={closeToDocsOffers}
              >
                {MAKE_NOTES_FROM_THESE_DOCS}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={closeToPath}
              >
                {SKIP_AND_OPEN_MY_BOOK}
              </button>
            </>
          ) : digestApproved && pathA && bookId ? (
            <>
              <button
                type="button"
                className="import-upload__submit"
                onClick={placeOnPlotTrack}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving…' : PUT_SCENES_ON_PLOT_TRACK}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={closeToDocsOffers}
                disabled={isSubmitting}
              >
                {MAKE_NOTES_FROM_THESE_DOCS}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={closeToPath}
                disabled={isSubmitting}
              >
                {SKIP_AND_OPEN_MY_BOOK}
              </button>
            </>
          ) : digestApproved ? (
            <>
              <button
                type="button"
                className="import-upload__submit"
                onClick={closeToDocsOffers}
              >
                {MAKE_NOTES_FROM_THESE_DOCS}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={closeToPath}
              >
                {SKIP_AND_OPEN_MY_BOOK}
              </button>
            </>
          ) : digest ? (
            <button
              type="button"
              className="import-upload__submit"
              onClick={handleApprove}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Approve digest'}
            </button>
          ) : result ? (
            result.accepted_count > 0 && !digest ? (
              <>
                <button
                  type="button"
                  className="import-upload__submit"
                  onClick={handleRetryDigest}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Writing digest…' : 'Write digest'}
                </button>
                <button
                  type="button"
                  className="import-upload__cancel"
                  onClick={() => closeWith()}
                  disabled={isSubmitting}
                >
                  Done
                </button>
              </>
            ) : (
              <button type="button" className="import-upload__submit" onClick={() => closeWith()}>
                Done
              </button>
            )
          ) : proseReport ? (
            <>
              <button
                type="button"
                className="import-upload__submit"
                onClick={handleConfirmProse}
                disabled={addScenesDisabled}
                aria-describedby={proseSceneCount === 0 ? needChapterId : undefined}
              >
                {isSubmitting
                  ? PROSE_CHECK_STEP_READING
                  : proseCheckAddLabel(proseSceneCount)}
              </button>
              {proseSceneCount === 0 && (
                <p id={needChapterId} className="import-upload__note">
                  {PROSE_CHECK_NEED_CHAPTER}
                </p>
              )}
              <button
                type="button"
                className="import-upload__cancel"
                onClick={handleSaveOnly}
                disabled={isSubmitting}
              >
                {PROSE_CHECK_SAVE_ONLY}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={() => {
                  setProseReport(null);
                  setProseStep('');
                  setError(null);
                }}
                disabled={isSubmitting}
              >
                {PROSE_CHECK_BACK}
              </button>
            </>
          ) : preview ? (
            <>
              <button
                type="button"
                className="import-upload__submit"
                onClick={handleProceed}
                disabled={!ready || isSubmitting || !previewHasReadableWords(preview)}
              >
                {isSubmitting
                  ? (proseStep === 'sorting' ? PROSE_CHECK_STEP_SORTING : PROSE_CHECK_STEP_READING)
                  : 'Proceed'}
              </button>
              {(showNoBook || couldNotSort) && (
                <button
                  type="button"
                  className="import-upload__cancel"
                  onClick={handleSaveOnly}
                  disabled={!ready || isSubmitting || !previewHasReadableWords(preview)}
                >
                  {PROSE_CHECK_SAVE_ONLY}
                </button>
              )}
              <button
                type="button"
                className="import-upload__cancel"
                onClick={() => {
                  setPreview(null);
                  setError(null);
                  setCouldNotSort(false);
                }}
                disabled={isSubmitting}
              >
                Back
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="import-upload__submit"
                onClick={handleContinue}
                disabled={!ready || isSubmitting}
              >
                {isSubmitting ? 'Checking…' : 'Continue'}
              </button>
              <button
                type="button"
                className="import-upload__cancel"
                onClick={() => closeWith()}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    <ConfirmModal {...modalProps} />
    </>,
    document.body
  );
}

export default ImportUploadModal;
